/**
 * Admin — Comments moderation.
 * Lists all comments and lets admins hide/show or delete.
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { MessageCircle, EyeOff, Eye, Trash2, Loader2, Send } from "lucide-react";
import { Header } from "@/components/rawan/Header";
import { Footer } from "@/components/rawan/Footer";
import { BackToDashboard } from "@/components/rawan/BackToDashboard";
import { useUserAuth } from "@/lib/user-auth";
import { useAuth } from "@/lib/auth";
import { adminListAllComments, deleteComment, setCommentHidden } from "@/lib/firebase-data";
import { notifyAll } from "@/lib/notify";

export const Route = createFileRoute("/admin/comments")({
  head: () => ({
    meta: [
      { title: "إدارة التعليقات — Rawan UI" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminCommentsPage,
});

type CommentRow = {
  id: string;
  threadId: string;
  uid: string;
  displayName: string;
  text: string;
  hidden: boolean;
  createdAt: number;
};

function AdminCommentsPage() {
  const { user, isAdmin: fbAdmin, loading: authLoading } = useUserAuth();
  const { isAuthed: localAdmin } = useAuth();
  // Admin if EITHER Firebase recognizes the email as admin OR the local
  // admin login (SHA-256 hash) is active. This guarantees the page opens
  // even if the Firebase mirror sign-in hasn't completed yet.
  const isAdmin = fbAdmin || localAdmin;
  const navigate = useNavigate();
  const [rows, setRows] = useState<CommentRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!isAdmin) {
      navigate({ to: "/" });
      return;
    }
    void refresh();
  }, [authLoading, user, isAdmin]);

  async function refresh() {
    setLoading(true);
    const list = (await adminListAllComments()) as CommentRow[];
    setRows(list);
    setLoading(false);
  }

  async function toggleHide(c: CommentRow) {
    const willHide = !c.hidden;
    await setCommentHidden(c.id, willHide);
    setRows((arr) => arr.map((x) => (x.id === c.id ? { ...x, hidden: willHide } : x)));
    // Auto-notify on approval (hidden -> visible).
    if (!willHide) {
      const preview = c.text.length > 120 ? c.text.slice(0, 117) + "…" : c.text;
      void notifyAll({
        title: `تعليق جديد من ${c.displayName}`,
        body: preview,
        url: "/",
      });
    }
  }

  async function pushNow(c: CommentRow) {
    const preview = c.text.length > 120 ? c.text.slice(0, 117) + "…" : c.text;
    const r = await notifyAll({
      title: `تعليق من ${c.displayName}`,
      body: preview,
      url: "/",
    });
    alert(r.ok ? `تم الإرسال: ${r.sent} | فشل: ${r.failed}` : `فشل: ${r.error}`);
  }
  async function remove(c: CommentRow) {
    if (!confirm("حذف هذا التعليق نهائياً؟")) return;
    await deleteComment(c.id);
    setRows((arr) => arr.filter((x) => x.id !== c.id));
  }

  if (!isAdmin) return null;

  return (
    <div className="min-h-dvh flex flex-col bg-background">
      <Header />
      <main className="flex-1 container mx-auto px-3 sm:px-4 py-6 sm:py-8">
        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <div className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl" style={{ background: "var(--gradient-primary)" }}>
            <MessageCircle className="h-5 w-5 text-primary-foreground" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="truncate text-lg sm:text-2xl font-extrabold">إدارة التعليقات</h1>
            <p className="text-xs text-muted-foreground">{rows.length} تعليق</p>
          </div>
          <BackToDashboard />
        </div>

        <div className="bg-card border border-border rounded-2xl shadow-card overflow-hidden">
          {loading ? (
            <div className="py-16 text-center text-muted-foreground"><Loader2 className="h-6 w-6 mx-auto animate-spin" /></div>
          ) : rows.length === 0 ? (
            <div className="py-16 text-center text-muted-foreground text-sm">لا توجد تعليقات</div>
          ) : (
            <ul className="divide-y divide-border">
              {rows.map((c) => (
                <li key={c.id} className={`p-3 sm:p-4 grid grid-cols-[minmax(0,1fr)_auto] gap-3 ${c.hidden ? "opacity-60" : ""}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-bold text-sm">{c.displayName}</span>
                      <span className="text-[11px] text-muted-foreground font-mono">{c.threadId}</span>
                      {c.hidden && <span className="text-[10px] px-2 py-0.5 rounded bg-yellow-500/15 text-yellow-600 border border-yellow-500/30">مخفي</span>}
                    </div>
                    <p className="text-sm whitespace-pre-wrap break-words">{c.text}</p>
                  </div>
                  <div className="flex flex-col gap-1.5 shrink-0">
                    <button onClick={() => toggleHide(c)} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-bold border border-border hover:bg-accent">
                      {c.hidden ? <><Eye className="h-3.5 w-3.5" /> إظهار</> : <><EyeOff className="h-3.5 w-3.5" /> إخفاء</>}
                    </button>
                    <button onClick={() => pushNow(c)} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-bold bg-primary/10 text-primary border border-primary/30 hover:bg-primary/20">
                      <Send className="h-3.5 w-3.5" /> إشعار
                    </button>
                    <button onClick={() => remove(c)} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-bold bg-destructive/10 text-destructive border border-destructive/30 hover:bg-destructive hover:text-destructive-foreground">
                      <Trash2 className="h-3.5 w-3.5" /> حذف
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
